/**
 * brand/BrandProvider.tsx
 *
 * Injects the active tenant's brand config as CSS custom properties on <html>.
 * Components read tokens via useBrand() (typed) or var(--brand-*) (CSS).
 *
 * Usage:
 *   <BrandProvider config={tenantBrandConfig}>
 *     <App />
 *   </BrandProvider>
 *
 * The config prop is a deep-merge of DEFAULT_BRAND + the tenant's overrides
 * from tenant_settings.brand_config_json. The merge is done in the hook that
 * fetches org settings — BrandProvider just applies whatever it receives.
 *
 * CSS variables injected (sample):
 *   --brand-primary
 *   --brand-primary-hover
 *   --brand-primary-subtle
 *   --brand-accent
 *   --brand-accent-hover
 *   --brand-accent-subtle
 *   --brand-danger
 *   --brand-danger-hover
 *   --brand-danger-subtle
 *   --brand-warning
 *   --brand-warning-subtle
 *   --brand-neutral-900  ... --brand-neutral-50
 *   --brand-white
 *   --brand-font-family
 *   --brand-font-family-display
 *   --brand-font-size-base
 *   --brand-radius-sm  --brand-radius-md  --brand-radius-lg  --brand-radius-full
 */

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react'
import { DEFAULT_BRAND }    from './config'
import type { BrandConfig } from './types'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND)

// ---------------------------------------------------------------------------
// CSS variable map
// ---------------------------------------------------------------------------

function buildCssVars(c: BrandConfig): Record<string, string> {
  const { colors: cl, typography: ty, shape: sh } = c
  return {
    '--brand-primary':         cl.primary,
    '--brand-primary-hover':   cl.primaryHover,
    '--brand-primary-subtle':  cl.primarySubtle,

    '--brand-accent':          cl.accent,
    '--brand-accent-hover':    cl.accentHover,
    '--brand-accent-subtle':   cl.accentSubtle,

    '--brand-danger':          cl.danger,
    '--brand-danger-hover':    cl.dangerHover,
    '--brand-danger-subtle':   cl.dangerSubtle,

    '--brand-warning':         cl.warning,
    '--brand-warning-subtle':  cl.warningSubtle,

    '--brand-neutral-900':     cl.neutral900,
    '--brand-neutral-700':     cl.neutral700,
    '--brand-neutral-500':     cl.neutral500,
    '--brand-neutral-300':     cl.neutral300,
    '--brand-neutral-100':     cl.neutral100,
    '--brand-neutral-50':      cl.neutral50,
    '--brand-white':           cl.white,

    '--brand-font-family':         ty.fontFamily,
    '--brand-font-family-display': ty.fontFamilyDisplay,
    '--brand-font-size-base':      ty.fontSizeBase,

    '--brand-radius-sm':   sh.radiusSm,
    '--brand-radius-md':   sh.radiusMd,
    '--brand-radius-lg':   sh.radiusLg,
    '--brand-radius-full': sh.radiusFull,
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface BrandProviderProps {
  config?:   BrandConfig
  children:  ReactNode
}

export function BrandProvider({ config = DEFAULT_BRAND, children }: BrandProviderProps): ReactNode {
  useEffect(() => {
    const vars = buildCssVars(config)
    const root = document.documentElement
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value)
    }
    // Update page title to tenant name
    document.title = config.name
    // Update favicon if provided
    if (config.assets.faviconUrl) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel~='icon']") ??
        Object.assign(document.createElement('link'), { rel: 'icon' })
      link.href = config.assets.faviconUrl
      document.head.appendChild(link)
    }
  }, [config])

  return (
    <BrandContext.Provider value={config}>
      {children}
    </BrandContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the active tenant's brand config.
 * Use this in components instead of importing DEFAULT_BRAND directly.
 *
 * @example
 * const brand = useBrand()
 * <img src={brand.assets.logoUrl} alt={brand.name} />
 */
export function useBrand(): BrandConfig {
  return useContext(BrandContext)
}
